import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Check, Copy, Loader2 } from 'lucide-react';
import { Alert, Badge } from '../ui';
import { ordersService } from '../../services/orders.service';
import { orderErrorMessage } from './orderError';
import {
  CANCELLABLE_STATUSES,
  REJECT_REASON_EMPTY_LABEL,
  averagePrice,
  avatarToneClass,
  customerDisplayName,
  customerInitials,
  executedNotional,
  expirationLabel,
  filledQuantity,
  fillRatio,
  fmtDateTime,
  fmtMoney,
  fmtQty,
  hasRejectReasonText,
  instrumentMarket,
  instrumentName,
  limitQuoteLabel,
  orderCurrency,
  orderCustomer,
  orderExpiration,
  orderExternalId,
  orderShowsRejectReason,
  orderTypeLabel,
  remainingQuantity,
  sideLabel,
  statusConfig,
} from './orderPresentation';

const CopyInline = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button type="button" onClick={handleCopy} className="ml-1 inline-flex items-center text-ink-300 hover:text-ink-600" aria-label="Copy">
      {copied ? <Check className="h-3 w-3 text-success-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

const InfoRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-ink-100 last:border-0">
    <span className="text-xs text-ink-500 shrink-0 w-36">{label}</span>
    <div className="flex items-center min-w-0 flex-1 justify-end text-right text-sm text-ink-800">
      {children}
    </div>
  </div>
);

const SectionCard = ({ title, children, className = '' }) => (
  <section className={`bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden ${className}`}>
    <div className="px-5 py-3 border-b border-ink-100">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">{title}</h3>
    </div>
    <div className="px-5">{children}</div>
  </section>
);

const Metric = ({ label, children }) => (
  <div className="px-4 py-3 min-w-0">
    <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-1">{label}</p>
    <div className="text-sm font-medium text-ink-900">{children}</div>
  </div>
);

const Money = ({ value, currency }) => (
  <span className="font-mono tnum">{value == null ? '—' : fmtMoney(value, currency)}</span>
);

const Qty = ({ value }) => (
  <span className="font-mono tnum">{value == null ? '—' : fmtQty(value)}</span>
);

const FillBar = ({ ratio }) => {
  if (ratio == null) return null;
  const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  return (
    <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden" aria-hidden="true">
      <div
        className={`h-full rounded-full ${ratio >= 1 ? 'bg-success-500' : 'bg-accent-500'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const IdPill = ({ label, value }) => (
  <div className="flex items-center gap-1 rounded-lg bg-ink-50 border border-ink-200 px-2.5 py-1.5 max-w-[16rem]">
    <span className="text-2xs text-ink-400 uppercase tracking-wider shrink-0">{label}</span>
    <span className="text-xs font-mono text-ink-700 truncate">{value}</span>
    <CopyInline value={value} />
  </div>
);

const OrderDetailView = ({
  order,
  onCancel,
  variant = 'page',
  showCancel,
}) => {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const statusCfg = statusConfig(order.status);
  const isPage = variant === 'page';
  const allowCancel = showCancel ?? !isPage;
  const canCancel = allowCancel && CANCELLABLE_STATUSES.has(order.status);
  const customer = orderCustomer(order);
  const name = customerDisplayName(customer);
  const filled = filledQuantity(order);
  const avg = averagePrice(order);
  const instrument = instrumentName(order);
  const currency = orderCurrency(order);
  const expiration = orderExpiration(order);
  const expirationText = expirationLabel(order);
  const market = instrumentMarket(order);
  const externalId = orderExternalId(order);
  const remaining = remainingQuantity(order);
  const notional = executedNotional(order);
  const ratio = fillRatio(order);
  const sideWord = sideLabel(order.side);
  const typeLabel = orderTypeLabel(order.type);
  const limitLabel = limitQuoteLabel(order);
  const subtitle = [instrument, market, typeLabel, expiration].filter(Boolean).join(' · ');
  const rejectTone = order.status === 'FAILED' ? 'warning' : 'danger';

  const handleCancel = async () => {
    setCancelError(null);
    setCancelling(true);
    try {
      await ordersService.cancelOrder(order.id);
      onCancel?.();
    } catch (err) {
      setCancelError(orderErrorMessage(err, 'The order could not be cancelled.'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className={isPage ? 'space-y-4' : 'pb-6'}>
      <div className={`bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden ${isPage ? '' : 'border-x-0 border-t-0 rounded-none shadow-none'}`}>
        <div
          className={`h-1.5 ${
            order.side === 'BUY'
              ? 'bg-success-500'
              : order.side === 'SELL'
                ? 'bg-danger-500'
                : 'bg-ink-300'
          }`}
        />
        <div className="px-5 py-4 flex items-start gap-4">
          <div
            className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${
              order.side === 'BUY'
                ? 'bg-success-100 text-success-700'
                : order.side === 'SELL'
                  ? 'bg-danger-100 text-danger-700'
                  : 'bg-ink-100 text-ink-600'
            }`}
          >
            {order.side === 'SELL' ? (
              <ArrowUpRight className="h-5 w-5" />
            ) : (
              <ArrowDownLeft className="h-5 w-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={`font-semibold text-ink-900 tracking-tight ${isPage ? 'text-2xl' : 'text-lg'}`}>
                {order.symbol || '—'}
              </h2>
              {sideWord && (
                <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold border ${
                  order.side === 'BUY'
                    ? 'bg-success-50 text-success-700 border-success-100'
                    : 'bg-danger-50 text-danger-700 border-danger-100'
                }`}>
                  {order.side}
                </span>
              )}
              <Badge tone={statusCfg.tone} dot>{statusCfg.label}</Badge>
            </div>
            {subtitle && <p className="text-sm text-ink-500 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
            {order.id && <IdPill label="ID" value={order.id} />}
            {externalId && <IdPill label="External" value={externalId} />}
          </div>
        </div>

        <div className={`grid border-t border-ink-100 divide-x divide-ink-100 ${
          isPage ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6' : 'grid-cols-2'
        }`}>
          <Metric label="Quantity"><Qty value={order.quantity} /></Metric>
          <Metric label="Filled">
            <Qty value={filled} />
            {ratio != null && <div className="mt-1.5 max-w-[7rem]"><FillBar ratio={ratio} /></div>}
          </Metric>
          <Metric label="Avg price"><Money value={avg} currency={currency} /></Metric>
          <Metric label="Limit"><span className="font-mono tnum">{limitLabel}</span></Metric>
          <Metric label="Currency">{currency || '—'}</Metric>
          <Metric label="Created">{fmtDateTime(order.createdAt)}</Metric>
        </div>
      </div>

      <div className={isPage ? 'space-y-4' : 'px-5 pt-4 space-y-4'}>
        {orderShowsRejectReason(order.status) && (
          <Alert tone={rejectTone} title="Reject reason">
            <span className={hasRejectReasonText(order.rejectReason) ? '' : 'text-ink-500'}>
              {hasRejectReasonText(order.rejectReason) ? order.rejectReason : REJECT_REASON_EMPTY_LABEL}
            </span>
          </Alert>
        )}

        {cancelError && (
          <Alert tone="danger">{cancelError}</Alert>
        )}

        <div className={`grid grid-cols-1 gap-4 ${isPage ? 'lg:grid-cols-2' : ''}`}>
          <SectionCard title="Customer">
            <div className="flex items-center gap-3 py-3 border-b border-ink-100">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarToneClass(order.customerId)}`}>
                {customerInitials(customer, order.customerId)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink-900 truncate">{name || 'Name unavailable'}</p>
                <p className="text-xs text-ink-500 truncate">{customer?.email || '—'}</p>
              </div>
            </div>
            <InfoRow label="Customer ID">
              <span className="font-mono text-xs break-all">
                {order.customerId ? (
                  <Link to={`/customers/${order.customerId}`} className="text-accent-700 hover:text-accent-800">
                    {order.customerId}
                  </Link>
                ) : '—'}
                {order.customerId && <CopyInline value={order.customerId} />}
              </span>
            </InfoRow>
            <InfoRow label="Platform reference">{customer?.referenceCode || '—'}</InfoRow>
            <InfoRow label="Partner reference">{customer?.externalReference || '—'}</InfoRow>
          </SectionCard>

          <SectionCard title="Order">
            <InfoRow label="Side">{order.side || '—'}</InfoRow>
            <InfoRow label="Type">{typeLabel || '—'}</InfoRow>
            <InfoRow label="Expiration">
              {expiration ? (
                <span>
                  <span className="font-medium">{expiration}</span>
                  {expirationText && (
                    <span className="block text-2xs text-ink-400 font-normal">{expirationText}</span>
                  )}
                </span>
              ) : '—'}
            </InfoRow>
            <InfoRow label="Market">{market || '—'}</InfoRow>
            <InfoRow label="Asset ID">
              <span className="font-mono text-xs break-all">{order.assetId || '—'}</span>
            </InfoRow>
            <InfoRow label="External ID">
              <span className="font-mono text-xs break-all">{externalId || '—'}</span>
            </InfoRow>
          </SectionCard>

          <SectionCard title="Economics">
            <InfoRow label="Quantity"><Qty value={order.quantity} /></InfoRow>
            <InfoRow label="Filled"><Qty value={filled} /></InfoRow>
            <InfoRow label="Remaining"><Qty value={remaining} /></InfoRow>
            <InfoRow label="Avg price"><Money value={avg} currency={currency} /></InfoRow>
            <InfoRow label="Limit"><span className="font-mono tnum">{limitLabel}</span></InfoRow>
            <InfoRow label="Currency">{currency || '—'}</InfoRow>
            {notional != null && (
              <InfoRow label="Fill value"><Money value={notional} currency={currency} /></InfoRow>
            )}
          </SectionCard>

          <SectionCard title="Timestamps">
            <InfoRow label="Created">{fmtDateTime(order.createdAt)}</InfoRow>
            <InfoRow label="Updated">{fmtDateTime(order.updatedAt)}</InfoRow>
          </SectionCard>
        </div>

        {canCancel && (
          <div className="pt-1">
            <button
              type="button"
              disabled={cancelling}
              onClick={handleCancel}
              className="btn btn-sm btn-danger disabled:opacity-50"
            >
              {cancelling && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Cancel order
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailView;
